/* oxlint-disable react/set-state-in-effect -- both effects below latch a one-shot
   flag before kicking off an async self-heal; there is nothing to derive during
   render, and the latch is what stops them looping. See the note on each. */
import { useEffect, useState } from 'react';
import { OrganizationRole } from 'src/context/types';
import { authClient } from 'src/services/auth/client';

export function useAuthSession() {
  const { data, isPending } = authClient.useSession();
  const [refreshedWhileEmpty, setRefreshedWhileEmpty] = useState(false);

  useEffect(() => {
    // A freshly signed-in session can momentarily resolve as empty (the session atom may
    // hydrate from a state that predates the sign-in response). One authoritative read
    // (disableCookieCache: true) heals it; never loop.
    if (isPending || data || refreshedWhileEmpty) return;
    setRefreshedWhileEmpty(true);
    void authClient.getSession({ query: { disableCookieCache: true } });
  }, [data, isPending, refreshedWhileEmpty]);

  const user = data?.user ?? null;
  const activeOrganizationId =
    (data?.session as { activeOrganizationId?: string | null } | undefined)?.activeOrganizationId ?? null;

  return {
    session: data ?? null,
    user,
    isPending,
    isVerified: Boolean(user?.emailVerified),
    activeOrganizationId,
    platformRole: ((user as { role?: string } | null)?.role ?? 'user') as 'user' | 'admin',
  };
}

export function useOrganizationRoleState(): { role: OrganizationRole | null; ready: boolean } {
  const { data, error, isPending, refetch } = authClient.useActiveMember();
  const [selfHung, setSelfHung] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    // The first get-active-member right after a setActive can arrive before the refreshed
    // session_data cookie is visible to the browser; better-auth then answers 401 and holds
    // data=null, which flips resolveContext to the first-branch fallback (owner/admin land
    // wrong). Retry ONCE by refreshing the session authoritatively first; never loop.
    if (error?.status !== 401 || selfHung) return;
    setSelfHung(true);
    setRetrying(true);
    void (async () => {
      try {
        await authClient.getSession({ query: { disableCookieCache: true } });
        await refetch({ query: { disableCookieCache: true } });
      } finally {
        setRetrying(false);
      }
    })();
  }, [error, selfHung, refetch]);

  // A 401 that has not yet been retried is not a settled answer — the role is unknown, not
  // absent. Treat that window as pending so callers do not redirect on a role they are
  // about to be given. The retry itself, once kicked off, is a second such window: `selfHung`
  // latches synchronously at the *start* of the retry, before either await resolves, so a
  // getSession()/refetch() round trip in flight must keep readiness false via `retrying`
  // rather than relying on `selfHung` alone.
  const healing = (error?.status === 401 && !selfHung) || retrying;

  return {
    role: (data?.role as OrganizationRole | undefined) ?? null,
    ready: !isPending && !healing,
  };
}

export function useOrganizationRole(): OrganizationRole | null {
  return useOrganizationRoleState().role;
}
